// API hooks для модуля notes.
//
// READ (Phase 1a offline): PowerSync — `notes` из локальной SQLite
// (`useNotesQuery` / `useNoteByIdQuery`).
// WRITE (Phase 2 offline): create / update / soft-delete — PowerSync
// `insertRow` / `updateRow` / `softDeleteRow` (upload-queue → notes write-handler).
// AI / versions / attachments / aiBlocks — остаются на tRPC (+ SSE).
//
// Hook-сигнатуры сохранены — UI-компоненты импортируют `useNotesList`,
// `useNote`, `useCreateNote` и т.д. без правок. Адаптеры маппят LocalNote
// (snake_case) → NoteDto (camelCase).
//
// SSE generation-progress + ai-block-stream — отдельные хуки, не tRPC.

import { DEFAULT_LOCALE } from "@studyhub/shared";
import type {
	Note as ContractNote,
	NoteListItem as ContractNoteListItem,
} from "@studyhub/shared/contracts/notes";
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { uuidv7 } from "uuidv7";
import { remoteStatusFromQuery } from "@/hooks/remote-status-from-query";
import type { RemoteStatus, SyncedDetailPhase } from "@/hooks/resolve-synced-detail-phase";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { useCurrentUser } from "@/hooks/use-session";
import { useSyncedDetail } from "@/hooks/use-synced-detail";
import { trackGenerationAccepted } from "@/lib/ecosystem-analytics";
import { insertRow, softDeleteRow, updateRow } from "@/lib/powersync";
import type { LocalNote } from "@/lib/powersync/queries/use-notes-query";
import { useNoteByIdQuery, useNotesQuery } from "@/lib/powersync/queries/use-notes-query";
import { usePowerSyncQueryShim } from "@/lib/powersync/query-shim";
import { trpcMutation, trpcQuery } from "@/lib/trpc-fetch";

/** Пустой TipTap-документ для manual note (зеркало server create-note). */
const EMPTY_NOTE_DOC = { type: "doc" as const, content: [] as Record<string, unknown>[] };

export type NoteDetailLevel = "summarized" | "in-depth" | "comprehensive" | "manual";

export type NoteAiBlockType =
	| "explain"
	| "summary"
	| "question"
	| "example"
	| "critique"
	| "translate"
	| "expand";

export type NoteGenerationStatus = "ready" | "generating" | "failed";

export interface NoteDto {
	id: string;
	materialId: string | null;
	/**
	 * Тема курса, для которой сгенерирован конспект (миграция 0083). Не-null =
	 * конспект собран из НЕСКОЛЬКИХ материалов темы → цитаты обязательны и их
	 * нельзя стрипать при открытии в редакторе (LEARNING-SYSTEM-VISION §15.2).
	 */
	sourceSubsectionId?: string | null;
	/** Имя материала из list JOIN — предпочтительнее PowerSync map (как flashcards). */
	materialName?: string | null;
	detailLevel: NoteDetailLevel;
	title: string;
	contentMarkdown: string;
	/** TipTap JSON document — реальный контент конспекта; null когда сервер не вернул. */
	content: unknown;
	language: string;
	/** Инкрементная версия конспекта (notes.version); растёт при регенерации/restore. */
	version: number;
	createdAt: string;
	updatedAt: string;
	aiModel?: string | null;
	promptVersion?: string | null;
	aiBlocksCount?: number;
	/**
	 * Статус AI-генерации. `generating` — placeholder, конспект ещё пишется;
	 * `failed` — workflow упал; `ready` — финализирован / manual note.
	 */
	generationStatus: NoteGenerationStatus;
	/** Текст ошибки при `failed`. */
	generationErrorMessage: string | null;
}

/**
 * Backend NoteListItem → UI NoteDto. Exported for unit tests
 * (list DTO → NoteDto с preview + materialName).
 */
export function adaptNoteListItem(n: ContractNoteListItem): NoteDto {
	return {
		id: n.id,
		materialId: n.materialId,
		materialName: n.materialName ?? null,
		detailLevel: n.detailLevel,
		title: n.title,
		contentMarkdown: n.preview,
		content: null,
		language: n.locale,
		version: 1,
		createdAt: n.createdAt,
		updatedAt: n.updatedAt,
		aiModel: n.aiModel,
		promptVersion: null,
		generationStatus: n.generationStatus,
		generationErrorMessage: n.generationErrorMessage,
	};
}

const DETAIL_LEVELS = new Set<NoteDetailLevel>([
	"summarized",
	"in-depth",
	"comprehensive",
	"manual",
]);

function asDetailLevel(raw: string | null | undefined): NoteDetailLevel {
	if (raw && DETAIL_LEVELS.has(raw as NoteDetailLevel)) return raw as NoteDetailLevel;
	return "manual";
}

function asGenerationStatus(raw: string | null | undefined): NoteGenerationStatus {
	if (raw === "generating" || raw === "failed" || raw === "ready") return raw;
	return "ready";
}

/** TipTap JSON в PowerSync — TEXT; безопасный parse. */
function parseNoteContent(raw: string | null): unknown {
	if (!raw) return null;
	try {
		return JSON.parse(raw) as unknown;
	} catch {
		return null;
	}
}

/** Preview для карточки списка (~200 символов plain-text). */
function buildNotePreview(plainText: string | null, maxLength = 200): string {
	const trimmed = (plainText ?? "").trim();
	if (trimmed.length <= maxLength) return trimmed;
	return `${trimmed.slice(0, maxLength).trimEnd()}…`;
}

/** LocalNote (PowerSync) → NoteDto для списка (без TipTap content). */
function adaptLocalNoteListItem(n: LocalNote): NoteDto {
	return {
		id: n.id,
		materialId: n.material_id ?? n.source_material_id ?? null,
		sourceSubsectionId: n.source_subsection_id ?? null,
		materialName: n.material_name ?? null,
		detailLevel: asDetailLevel(n.detail_level),
		title: n.title,
		contentMarkdown: buildNotePreview(n.plain_text),
		content: null,
		language: n.locale ?? DEFAULT_LOCALE,
		version: n.version ?? 1,
		createdAt: n.created_at,
		updatedAt: n.updated_at,
		aiModel: n.ai_model ?? null,
		promptVersion: null,
		generationStatus: asGenerationStatus(n.generation_status),
		generationErrorMessage: n.generation_error_message ?? null,
	};
}

/** LocalNote (PowerSync) → NoteDto для detail (с TipTap content). */
function adaptLocalNote(n: LocalNote): NoteDto {
	return {
		id: n.id,
		materialId: n.material_id ?? n.source_material_id ?? null,
		sourceSubsectionId: n.source_subsection_id ?? null,
		detailLevel: asDetailLevel(n.detail_level),
		title: n.title,
		contentMarkdown: n.plain_text ?? "",
		content: parseNoteContent(n.content),
		language: n.locale ?? DEFAULT_LOCALE,
		version: n.version ?? 1,
		createdAt: n.created_at,
		updatedAt: n.updated_at,
		aiModel: n.ai_model ?? null,
		promptVersion: n.prompt_version ?? null,
		generationStatus: asGenerationStatus(n.generation_status),
		generationErrorMessage: n.generation_error_message ?? null,
	};
}

/** tRPC `notes.get` → NoteDto (фолбэк, пока PowerSync не догнал). */
function adaptContractNote(n: ContractNote): NoteDto {
	return {
		id: n.id,
		materialId: n.materialId,
		detailLevel: n.detailLevel,
		title: n.title,
		contentMarkdown: n.plainText,
		content: n.content,
		language: n.locale,
		version: n.version,
		createdAt: n.createdAt,
		updatedAt: n.updatedAt,
		aiModel: n.aiModel,
		promptVersion: n.promptVersion,
		generationStatus: n.generationStatus,
		generationErrorMessage: n.generationErrorMessage,
	};
}

export function isNoteGenerationInFlight(status: NoteGenerationStatus | undefined): boolean {
	return status === "generating";
}

/**
 * Пустой TipTap-документ: `null` или `doc` без `content` / с `content: []`.
 * Короткий конспект с узлами в `content[]` — не пустой: его не добираем с сервера.
 */
export function isNoteContentEmpty(content: unknown): boolean {
	if (content == null) return true;
	if (typeof content !== "object") return false;
	const doc = content as { type?: unknown; content?: unknown };
	if (!("content" in doc) || doc.content == null) return true;
	return Array.isArray(doc.content) && doc.content.length === 0;
}

/**
 * Редактор/автосейв нельзя монтировать: фаза ещё не ready, либо ready-строка
 * без тела, пока `notes.get` в полёте (иначе starter запишется поверх текста).
 */
/**
 * Показывать ли вместо редактора экран фазы (скелетон / офлайн / не найдено /
 * ошибка).
 *
 * Чистая функция, а не выражение по месту: ровно здесь уже был инцидент. В
 * `notes-editor.tsx` стояло `const screen = <PhaseScreen …/>; if (screen)
 * return screen;` — условие всегда истинно, потому что JSX даёт объект, а
 * `null` компонент отдавал уже внутри себя. Итог: у любого существующего
 * конспекта редактор не монтировался и страница была пустой, без ошибок в
 * консоли. Правило, вынесенное в функцию, проверяется тестом, а не глазами.
 */
export function shouldShowNotePhaseScreen(opts: {
	noteId: string;
	demo?: string;
	phase: SyncedDetailPhase;
}): boolean {
	if (opts.noteId === "new" || opts.demo) return false;
	return opts.phase !== "ready";
}

export function shouldMountNoteEditor(opts: {
	phase: SyncedDetailPhase;
	content: unknown;
	remoteStatus: RemoteStatus;
	generationStatus?: NoteGenerationStatus;
}): boolean {
	if (opts.phase !== "ready") return false;
	if (opts.generationStatus === "generating") return true;
	return !(isNoteContentEmpty(opts.content) && opts.remoteStatus === "pending");
}

/**
 * PowerSync primary; terminal remote перекрывает устаревший generating-local
 * (лаг download-sync после готовности конспекта).
 */
export function mergeNoteDetail(
	local: NoteDto | undefined,
	remote: NoteDto | undefined,
): NoteDto | undefined {
	if (!local) return remote;
	if (!remote) return local;
	if (
		isNoteGenerationInFlight(local.generationStatus) &&
		!isNoteGenerationInFlight(remote.generationStatus)
	) {
		return remote;
	}
	if (isNoteContentEmpty(local.content) && !isNoteContentEmpty(remote.content)) {
		return {
			...local,
			content: remote.content,
			contentMarkdown: remote.contentMarkdown || local.contentMarkdown,
			generationStatus: remote.generationStatus,
			generationErrorMessage: remote.generationErrorMessage,
		};
	}
	return local;
}

type NotesGetCache = { note: ContractNote; aiBlocks: unknown[] };

/** Seed notes.get cache before navigate so the editor opens without waiting for PowerSync. */
export function seedNoteGetCache(qc: QueryClient, note: ContractNote): void {
	qc.setQueryData<NotesGetCache>(["notes", "get", note.id], { note, aiBlocks: [] });
}

/** Prefetch notes.get and seed the same cache key `useNote` reads. */
export async function prefetchAndSeedNoteGetCache(qc: QueryClient, noteId: string): Promise<void> {
	const data = await trpcQuery<NotesGetCache>("notes.get", { id: noteId });
	seedNoteGetCache(qc, data.note);
}

export interface NoteDetailResult {
	data: NoteDto | undefined;
	phase: SyncedDetailPhase;
	remoteStatus: RemoteStatus;
	refetch: () => void;
	isLoading: boolean;
	isError: boolean;
}

// ─── Queries (PowerSync READ) ─────────────────────────────────────────

export function useNotesList() {
	const user = useCurrentUser();
	const query = useNotesQuery(user?.id);
	// PowerSync реактивно обновит status при sync — polling не нужен.
	const data = useMemo(
		() => ({ items: (query.data ?? []).map(adaptLocalNoteListItem) }),
		[query.data],
	);
	return usePowerSyncQueryShim(query, data);
}

export function useNote(id: string | undefined): NoteDetailResult {
	const localQuery = useNoteByIdQuery(id);
	const isOnline = useOnlineStatus();
	const local = useMemo(() => {
		if (!id) return undefined;
		const row = localQuery.data?.[0];
		return row ? adaptLocalNote(row) : undefined;
	}, [localQuery.data, id]);

	const hasLocal = Boolean(local);
	const localInFlight = isNoteGenerationInFlight(local?.generationStatus);
	const localContentEmpty = isNoteContentEmpty(local?.content);
	const remoteEnabled =
		Boolean(id) && isOnline && (!hasLocal || localInFlight || localContentEmpty);

	const remoteQuery = useQuery({
		queryKey: ["notes", "get", id],
		queryFn: () => trpcQuery<NotesGetCache>("notes.get", { id }),
		enabled: remoteEnabled,
		retry: false,
		staleTime: 0,
		refetchInterval: localInFlight ? 3_000 : false,
	});

	const remoteUi = useMemo(() => {
		if (!remoteQuery.data?.note) return undefined;
		return adaptContractNote(remoteQuery.data.note);
	}, [remoteQuery.data]);

	const data = useMemo(() => mergeNoteDetail(local, remoteUi), [local, remoteUi]);

	const remoteStatus = remoteStatusFromQuery({
		isFetching: remoteQuery.isFetching,
		isError: remoteQuery.isError,
		error: remoteQuery.error,
		hasData: Boolean(remoteQuery.data?.note),
		enabled: remoteEnabled,
	});

	const detail = useSyncedDetail({
		hasData: Boolean(data),
		localLoading: Boolean(id) && localQuery.isLoading,
		localError: localQuery.error != null,
		remoteStatus,
	});

	// «new» / нет id — сущности нет, фаза ready: редактор бутстрапит пустую заметку.
	const phase: SyncedDetailPhase = id ? detail.phase : "ready";

	return {
		data,
		phase,
		remoteStatus,
		isLoading: phase === "loading",
		isError: phase === "load_failed",
		refetch: () => {
			void localQuery.refresh?.();
			void remoteQuery.refetch();
		},
	};
}

// ─── Mutations ────────────────────────────────────────────────────────

interface CreateNoteVars {
	materialId?: string;
	title: string;
	detailLevel?: NoteDetailLevel;
	locale?: string;
}

export function useCreateNote() {
	const qc = useQueryClient();
	const { i18n } = useTranslation();
	const user = useCurrentUser();
	return useMutation({
		// Локальный INSERT → мгновенный id для навигации /notes/:id; upload-queue
		// пушит PUT на notes write-handler (CASL ownership server-side).
		mutationFn: async (vars: CreateNoteVars) => {
			if (!user?.id) throw new Error("NOT_AUTHENTICATED");
			const id = uuidv7();
			const locale = vars.locale ?? i18n.language ?? "en";
			const now = new Date().toISOString();
			const detailLevel = vars.detailLevel ?? "manual";
			await insertRow("notes", {
				id,
				user_id: user.id,
				title: vars.title,
				material_id: vars.materialId ?? null,
				detail_level: detailLevel,
				locale,
				content: JSON.stringify(EMPTY_NOTE_DOC),
				plain_text: "",
				generated_by_ai: 0,
				applied_disclaimer: 0,
				version: 1,
				generation_status: "ready",
				generation_error_message: null,
				deleted_at: null,
			});
			seedNoteGetCache(qc, {
				id,
				userId: user.id,
				materialId: vars.materialId ?? null,
				title: vars.title,
				detailLevel,
				content: EMPTY_NOTE_DOC,
				plainText: "",
				locale,
				appliedDisclaimer: false,
				version: 1,
				parentNoteId: null,
				aiModel: null,
				promptVersion: null,
				generationTraceId: null,
				generationStatus: "ready",
				generationErrorMessage: null,
				createdAt: now,
				updatedAt: now,
				deletedAt: null,
			});
			return { id };
		},
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ["notes", "list"] });
		},
	});
}

export function useDeleteNote() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (id: string) => {
			await softDeleteRow("notes", id);
			return { ok: true as const };
		},
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ["notes", "list"] });
			void qc.invalidateQueries({ queryKey: ["trash"] });
		},
	});
}

interface UpdateNoteVars {
	id: string;
	title?: string;
	content?: unknown;
	plainText?: string;
}

export function useUpdateNote() {
	const qc = useQueryClient();
	return useMutation({
		// Offline-autosave: локальный PATCH; write-handler на сервере делает
		// checkpointNoteVersion при наличии content (как tRPC update-note).
		mutationFn: async ({ id, ...patch }: UpdateNoteVars) => {
			const row: Record<string, string | number | null | undefined> = {};
			if (patch.title !== undefined) row.title = patch.title;
			if (patch.content !== undefined) {
				row.content =
					typeof patch.content === "string" ? patch.content : JSON.stringify(patch.content);
			}
			if (patch.plainText !== undefined) row.plain_text = patch.plainText;
			await updateRow("notes", id, row);
			const savedAt = new Date().toISOString();
			return { ok: true as const, savedAt };
		},
		onSuccess: (_data, vars) => {
			void qc.invalidateQueries({ queryKey: ["notes", "get", vars.id] });
		},
	});
}

interface GenerateNoteVars {
	materialId: string;
	detailLevel: "summarized" | "in-depth" | "comprehensive";
	locale?: string;
	/** Свободные пожелания пользователя к конспекту (до 2000 символов). */
	customInstructions?: string;
}

export function useGenerateNote() {
	const qc = useQueryClient();
	const { i18n } = useTranslation();
	return useMutation({
		mutationFn: (vars: GenerateNoteVars) =>
			trpcMutation<{ jobId: string; status: "queued" }>("notes.generateFromMaterial", {
				materialId: vars.materialId,
				detailLevel: vars.detailLevel,
				// Сервер всё равно переопределит на `materials.language` (Inngest
				// effectiveLocale), но шлём честный UI-locale на случай если
				// у материала локаль не определена (NULL).
				locale: vars.locale ?? i18n.language ?? "en",
				customInstructions: vars.customInstructions || undefined,
			}).then((r) => ({ id: r.jobId, status: "queued" as const })),
		onSuccess: (data, vars) => {
			trackGenerationAccepted("summary", data);
			void qc.invalidateQueries({ queryKey: ["notes", "list"] });
			void qc.invalidateQueries({
				queryKey: ["notes", "list", "material", vars.materialId],
			});
		},
	});
}

interface AiBlockCreateVars {
	noteId: string;
	position: number;
	type: NoteAiBlockType;
	prompt: string;
	selectionText?: string;
}

interface AiBlockCreateResult {
	block: { id: string; status: "streaming" | "ready" | "failed" | "cancelled" };
	/** Относительный SSE-путь `/api/notes/ai-blocks/:id/stream`. */
	streamUrl: string;
	/** Короткоживущий HMAC-токен для авторизации SSE (single-use). */
	streamToken: string;
}

export function useCreateAiBlock() {
	return useMutation({
		mutationFn: (vars: AiBlockCreateVars) =>
			trpcMutation<AiBlockCreateResult>("notes.aiBlocks.create", vars),
	});
}

export function useRegenerateAiBlock() {
	return useMutation({
		mutationFn: (vars: { id: string; prompt?: string; selectionText?: string | null }) =>
			trpcMutation<AiBlockCreateResult>("notes.aiBlocks.regenerate", vars),
	});
}

export function useDeleteAiBlock() {
	return useMutation({
		mutationFn: (blockId: string) =>
			trpcMutation<{ ok: true }>("notes.aiBlocks.delete", { id: blockId }),
	});
}

// ─── Attachments (изображения в конспекте) ────────────────────────────

export interface AttachmentUploadVars {
	noteId: string;
	mimeType: string;
	sizeBytes: number;
}

export interface AttachmentUploadResult {
	uploadUrl: string;
	uploadHeaders: Record<string, string>;
	downloadUrl: string;
	/** Стабильный `s3://bucket/key` — хранится в атрибуте TipTap Image-ноды. */
	storageRef: string;
	expiresAt: string;
}

/**
 * Запросить pre-signed PUT URL для загрузки изображения в конспект
 * (`notes.attachmentRequestUpload`). Клиент затем PUT'ит файл на uploadUrl
 * с uploadHeaders и вставляет Image-ноду с storageRef.
 */
export function useAttachmentRequestUpload() {
	return useMutation({
		mutationFn: (vars: AttachmentUploadVars) =>
			trpcMutation<AttachmentUploadResult>("notes.attachmentRequestUpload", vars),
	});
}

/**
 * Ленивый re-presign изображения конспекта по storageRef (`notes.resolveAttachment`).
 * Вызывается из TipTap Image NodeView при монтировании — presigned GET TTL (7д)
 * истекает, конспект живёт дольше, поэтому URL пересоздаётся при каждом рендере.
 * Прямой вызов (не reactive query) — резолв one-shot на монтирование ноды.
 */
export function resolveNoteAttachment(
	storageRef: string,
): Promise<{ downloadUrl: string; expiresAt: string }> {
	return trpcQuery<{ downloadUrl: string; expiresAt: string }>("notes.resolveAttachment", {
		storageRef,
	});
}

// ─── Glossary (из конспекта) ──────────────────────────────────────────
/** @deprecated импортируй из `@/components/glossary/_api`. */
export {
	type CreateGlossaryTermVars,
	useCreateGlossaryTerm,
} from "@/components/glossary/_api";

// ─── Versions ─────────────────────────────────────────────────────────

interface VersionDto {
	id: string;
	version: number;
	reason: "ai_regenerate" | "manual_save" | "restore";
	author: string;
	createdAt: string;
	aiModel: string | null;
	/** Заголовок snapshot'а на момент создания версии. */
	title: string;
	/** TipTap JSON snapshot — реальный контент версии (для read-only превью). */
	content: unknown;
	/** Денормализованный plain-text snapshot. */
	plainText: string;
}

// Backend NoteVersion присылает `createdBy` (UUID) + `createdAt`. UI-слой ждёт
// `author` (человекочитаемое) — пока маппим напрямую из contract-полей. Сервер
// отдаёт полный snapshot (title/content/plainText) — используем для превью.
interface ContractVersion {
	id: string;
	version: number;
	reason: "ai_regenerate" | "manual_save" | "restore";
	createdBy: string;
	createdAt: string;
	aiModel: string | null;
	title: string;
	content: unknown;
	plainText: string;
}

export function useNoteVersions(noteId: string | undefined) {
	return useQuery({
		queryKey: ["notes", "versions", noteId],
		queryFn: async () => {
			const res = await trpcQuery<{ versions: ContractVersion[] }>("notes.versions.list", {
				noteId,
			});
			return {
				versions: res.versions.map(
					(v): VersionDto => ({
						id: v.id,
						version: v.version,
						reason: v.reason,
						author: v.createdBy,
						createdAt: v.createdAt,
						aiModel: v.aiModel,
						title: v.title,
						content: v.content,
						plainText: v.plainText,
					}),
				),
			};
		},
		enabled: !!noteId,
		staleTime: 60_000,
	});
}

interface RegenerateVersionVars {
	noteId: string;
	detailLevel: "summarized" | "in-depth" | "comprehensive";
	/** Свободные пожелания пользователя к новой версии конспекта. */
	customInstructions?: string;
}

/**
 * Перегенерация material-backed конспекта как НОВОЙ ВЕРСИИ текущего note
 * (`notes.regenerateVersion`): сервер снапшотит текущий content в noteVersions
 * (reason='ai_regenerate'), version++ и эмитит Inngest `notes/regenerate.requested`.
 * Прогресс/готовность — через Socket.IO (`note:generation_progress`/`note:ready`).
 * НЕ создаёт дубль-конспект (в отличие от `notes.generateFromMaterial`).
 */
export function useRegenerateVersion() {
	const qc = useQueryClient();
	return useMutation({
		// Сервер возвращает { note, version } (snapshot + placeholder новой версии);
		// UI достаточно факта успеха — детали прилетят через invalidate + Socket.IO.
		mutationFn: (vars: RegenerateVersionVars) =>
			trpcMutation<{ note: ContractNote; version: { id: string } }>("notes.regenerateVersion", {
				noteId: vars.noteId,
				detailLevel: vars.detailLevel,
				customInstructions: vars.customInstructions || undefined,
			}),
		onSuccess: (_data, vars) => {
			void qc.invalidateQueries({ queryKey: ["notes", "get", vars.noteId] });
			void qc.invalidateQueries({ queryKey: ["notes", "versions", vars.noteId] });
			void qc.invalidateQueries({ queryKey: ["notes", "list"] });
		},
	});
}

export function useRestoreVersion() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ noteId, versionId }: { noteId: string; versionId: string }) =>
			trpcMutation<{ ok: true }>("notes.versions.restore", {
				noteId,
				versionId,
			}),
		onSuccess: (_data, vars) => {
			void qc.invalidateQueries({ queryKey: ["notes", "get", vars.noteId] });
			void qc.invalidateQueries({
				queryKey: ["notes", "versions", vars.noteId],
			});
		},
	});
}
